// Metafield filters (facets-metafield-filter) work on the whole collection: the filter data of every
// product comes from the collection.filter-data template, and the matching cards are taken from the
// collection pages that hold them. Shared by every facet-form on the page (sidebar, drawer, sticky).
const customFacetState = {
  data: new Map(),
  pages: new Map(),
  run: 0,
  timer: null,
  gridFiltered: false,
};

const customFacetHandle = (value) =>
  value
    .toLowerCase()
    .trim()
    .replace(/['"]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const customFacetEscape = (value) =>
  String(value).replace(
    /[&<>"']/g,
    (char) =>
      ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[
        char
      ],
  );

if (!customElements.get("facet-form")) {
  customElements.define(
    "facet-form",
    class FacetForm extends BaseElementMixin(HTMLFormElement) {
      constructor() {
        super();

        this.dirty = false;
        this.cachedMap = new Map();
        this.isMobile = theme.config.mqlSmall || theme.config.isTouch;
        this.motionReduced =
          theme.config.motionReduced || this.hasAttribute("motion-reduced");
      }

      connectedCallback() {
        super.connectedCallback();

        this.on(this, "change", this.onFormChange);
        this.on(this, "submit", this.onFormSubmit);

        this.syncCustomFiltersFromURL();
        this.applyCustomMetafieldFilters();
        this.renderCustomActiveFacets();
        this.setupCustomFilterListeners();
        this.sortFacetOptionsNaturally();
        setTimeout(() => this.sortFacetOptionsNaturally(), 50);
      }

      disconnectedCallback() {
        super.disconnectedCallback();

        this.renderAbortController?.abort();
      }

      getAnimationParams() {
        return {
          distance: this.isMobile ? 30 : 50,
          duration: this.motionReduced ? 0.001 : this.isMobile ? 0.3 : 0.5,
          staggerDelay: this.motionReduced ? 0 : this.isMobile ? 0.05 : 0.1,
        };
      }

      getVisibleItems(items) {
        const viewportHeight = window.innerHeight + window.scrollY;
        const visible = [];
        const invisible = [];

        items.forEach((item) => {
          if (item.style.display === "none") return;
          const rect = item.getBoundingClientRect();
          const isVisible = rect.top < viewportHeight && rect.bottom > 0;

          if (isVisible) {
            visible.push(item);
          } else {
            invisible.push(item);
          }
        });

        return { visible, invisible };
      }

      onFormChange(event) {
        if (
          event &&
          event.target &&
          event.target.hasAttribute("data-custom-filter")
        ) {
          const target = event.target;
          const targetName = target.name;
          const targetVal = target.value;
          const isChecked = target.checked;

          // Immediately sync all matching inputs across desktop sidebar, mobile drawer, and sticky facets
          document
            .querySelectorAll("input[data-custom-filter]")
            .forEach((input) => {
              if (input.name === targetName && input.value === targetVal) {
                input.checked = isChecked;
              }
            });

          // Shopify ignores these params, so the page is filtered here without asking the server
          this.updateCustomFilterURL(target);
          this.applyCustomMetafieldFilters();
          this.renderCustomActiveFacets();
          return;
        }

        this.dirty = true;
        this.applyCustomMetafieldFilters();
        this.renderCustomActiveFacets();
        this.dispatchEvent(new Event("submit", { cancelable: true }));
      }

      onFormSubmit(event) {
        event.preventDefault();
        if (!this.dirty) return;

        const url = this.buildUrl().toString();
        this.renderSection(url, event);
      }

      buildUrl() {
        // const searchParams = new URLSearchParams(new FormData(this));
        const formData = new FormData(this);
        const url = new URL(this.action);

        url.search = "";
        // searchParams.forEach((value, key) => url.searchParams.append(key, value));

        // ['page', 'filter.v.price.gte', 'filter.v.price.lte'].forEach((item) => {
        //   if (url.searchParams.get(item) === '') {
        //     url.searchParams.delete(item);
        //   }
        // });

        for (const [key, value] of formData.entries()) {
          const trimmedValue = value.trim();
          if (trimmedValue === "") continue;

          const existingValues = url.searchParams.getAll(key);
          if (!existingValues.includes(trimmedValue)) {
            url.searchParams.append(key, trimmedValue);
          }
        }

        // Ensure all checked custom filters across forms are included in URL params
        document
          .querySelectorAll(
            'form[is="facet-form"] input[data-custom-filter]:checked',
          )
          .forEach((cb) => {
            if (cb.name && cb.value) {
              const val = cb.value.trim();
              if (val && !url.searchParams.getAll(cb.name).includes(val)) {
                url.searchParams.append(cb.name, val);
              }
            }
          });

        url.searchParams.set("section_id", theme.utils.sectionId(this));
        return url;
      }

      updateURLHash(url) {
        const clonedUrl = new URL(url);
        clonedUrl.searchParams.delete("section_id");
        history.replaceState({}, "", clonedUrl.toString());
      }

      beforeRenderSection() {
        const container = document.getElementById("ProductGridContainer");
        const items = container.querySelectorAll(
          ".product-card:not(.custom-facet-hidden)",
        );

        const { distance, duration, staggerDelay } = this.getAnimationParams();
        const { visible: visibleItems, invisible: inVisibleItems } =
          this.getVisibleItems(items);

        Motion.timeline([
          [
            visibleItems,
            { y: distance, opacity: 0, visibility: "hidden" },
            { duration: duration, delay: Motion.stagger(staggerDelay) },
          ],
          [
            inVisibleItems,
            { y: distance, opacity: 0, visibility: "hidden" },
            { duration: duration },
          ],
          [
            container,
            { y: distance, opacity: 0 },
            { duration: duration, easing: "linear" },
          ],
        ]);
        items.forEach((item) => item.style.removeProperty("transform"));

        let headerHeight = 0;
        if (!theme.config.mqlSmall && document.querySelector("header.header")) {
          headerHeight = Math.round(
            document.querySelector("header.header").clientHeight,
          );
        }
        setTimeout(() => {
          const target = document.querySelector(".collection");
          window.scrollTo({
            top:
              target.getBoundingClientRect().top +
              window.scrollY -
              (theme.config.isTouch || theme.config.mqlSmall ? 95 : 20) -
              headerHeight,
            behavior: theme.config.motionReduced ? "auto" : "smooth",
          });

          const drawer = document.getElementById("FacetDrawer");
          if (drawer) drawer.classList.add("loading");
        }, 100);
      }

      afterRenderSection() {
        this.applyCustomMetafieldFilters();
        this.renderCustomActiveFacets();
        const container = document.getElementById("ProductGridContainer");
        const items = container.querySelectorAll(
          ".product-card:not(.custom-facet-hidden)",
        );

        const { distance, duration, staggerDelay } = this.getAnimationParams();
        const { visible: visibleItems, invisible: inVisibleItems } =
          this.getVisibleItems(items);

        Motion.timeline([
          [
            container,
            { y: [distance, 0], opacity: [0, 1] },
            { duration: duration, easing: "linear" },
          ],
          [
            visibleItems,
            {
              y: [distance, 0],
              opacity: [0, 1],
              visibility: ["hidden", "visible"],
            },
            { duration: duration, delay: Motion.stagger(staggerDelay) },
          ],
          [
            inVisibleItems,
            {
              y: [distance, 0],
              opacity: [0, 1],
              visibility: ["hidden", "visible"],
            },
            { duration: duration },
          ],
        ]);
        items.forEach((item) => item.style.removeProperty("transform"));

        const drawer = document.getElementById("FacetDrawer");
        if (drawer) drawer.classList.remove("loading");

        document.dispatchEvent(new CustomEvent("collection:reloaded"));
        this.applyCustomMetafieldFilters();
        this.renderCustomActiveFacets();
        this.sortFacetOptionsNaturally();
      }

      renderSection(url, event) {
        this.renderAbortController?.abort();
        this.renderAbortController = new AbortController();
        const { signal } = this.renderAbortController;

        this.cachedMap.has(url)
          ? this.renderSectionFromCache(url, event, signal)
          : this.renderSectionFromFetch(url, event, signal);

        if (this.hasAttribute("data-history")) this.updateURLHash(url);

        this.dirty = false;
      }

      renderSectionFromFetch(url, event, signal) {
        this.beforeRenderSection();

        const delay = this.getAnimationParams().duration * 1000;
        const start = performance.now();

        fetch(url, { signal })
          .then((response) => response.text())
          .then((responseText) => {
            const remaining = Math.max(0, delay - (performance.now() - start));

            setTimeout(() => {
              if (signal.aborted) return;

              const parsedHTML = new DOMParser().parseFromString(
                responseText,
                "text/html",
              );
              this.applyRender(parsedHTML, event);

              theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.facetUpdate, {
                responseText,
                parsedHTML,
              });

              this.cachedMap.set(url, responseText);
              if (this.cachedMap.size > 50) {
                this.cachedMap.delete(this.cachedMap.keys().next().value);
              }

              this.afterRenderSection();
            }, remaining);
          })
          .catch((error) => {
            if (error.name !== "AbortError") console.error(error);
          });
      }

      renderSectionFromCache(url, event, signal) {
        this.beforeRenderSection();

        const responseText = this.cachedMap.get(url);
        const delay = this.getAnimationParams().duration * 1000;

        setTimeout(() => {
          if (signal.aborted) return;

          const parsedHTML = new DOMParser().parseFromString(
            responseText,
            "text/html",
          );
          this.applyRender(parsedHTML, event);

          theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.facetUpdate, {
            responseText,
            parsedHTML,
          });

          this.afterRenderSection();
        }, delay);
      }

      applyRender(parsedHTML, event) {
        this.renderFilters(parsedHTML, event);
        this.renderFiltersActive(parsedHTML);
        this.renderProductGridContainer(parsedHTML);
        this.renderProductCount(parsedHTML);
        this.renderSortBy(parsedHTML);
        this.applyCustomMetafieldFilters();
        this.renderCustomActiveFacets();
      }

      renderFilters(parsedHTML, event) {
        const checkedCustomFilters = [];
        document
          .querySelectorAll("input[data-custom-filter]:checked")
          .forEach((input) => {
            checkedCustomFilters.push({ name: input.name, value: input.value });
          });

        const facetElements = parsedHTML.querySelectorAll(
          "#FacetFiltersContainer [data-filter], #MobileFacetFiltersContainer [data-filter]",
        );

        const matchesIndex = (element) => {
          const jsFilter = event
            ? event.target.closest("[data-filter]")
            : undefined;
          return jsFilter
            ? element.getAttribute("data-index") ===
                jsFilter.getAttribute("data-index")
            : false;
        };
        const facetsToRender = Array.from(facetElements).filter(
          (element) => !matchesIndex(element),
        );

        facetsToRender.forEach((element) => {
          const filter = document.querySelector(
            `[data-filter][data-index="${element.getAttribute("data-index")}"]`,
          );
          if (filter !== null) {
            if (filter.tagName === "DETAILS") {
              filter.querySelector("summary + *").innerHTML =
                element.querySelector("summary + *").innerHTML;
            } else {
              filter.innerHTML = element.innerHTML;
            }
          }
        });

        checkedCustomFilters.forEach(({ name, value }) => {
          document
            .querySelectorAll("input[data-custom-filter]")
            .forEach((input) => {
              if (input.name === name && input.value === value) {
                input.checked = true;
              }
            });
        });
        this.sortFacetOptionsNaturally();
      }

      renderFiltersActive(parsedHTML) {
        this._updateSection(parsedHTML, "FacetFiltersActive");
        this.renderCustomActiveFacets();
      }

      renderProductGridContainer(parsedHTML) {
        customFacetState.gridFiltered = false;
        this._updateSection(
          parsedHTML,
          "ProductGridContainer",
          (newContent) => {
            newContent
              .querySelector("motion-list")
              ?.setAttribute("motion-reduced", "");
          },
        );
      }

      renderProductCount(parsedHTML) {
        this._updateSection(parsedHTML, "ProductCount");
      }

      renderSortBy(parsedHTML) {
        this._updateSection(
          parsedHTML,
          "SortByContainer",
          (newContent, oldContainer) => {
            const oldFacetSort = oldContainer.querySelector("facet-sort");
            const newFacetSort = newContent.querySelector("facet-sort");
            if (!oldFacetSort || !newFacetSort) return;

            if (oldFacetSort.hasAttribute("style")) {
              newFacetSort.setAttribute(
                "style",
                oldFacetSort.getAttribute("style"),
              );
            }
          },
        );
      }

      _updateSection(parsedHTML, sectionId, modifier = null) {
        const container = document.getElementById(sectionId);
        if (!container) return false;

        const newContent = parsedHTML.getElementById(sectionId);
        if (!newContent) return false;

        // Apply custom modifications if provided
        modifier?.(newContent, container);

        container.innerHTML = newContent.innerHTML;
        return true;
      }

      syncCustomFiltersFromURL() {
        const urlParams = new URLSearchParams(window.location.search);
        document
          .querySelectorAll("input[data-custom-filter]")
          .forEach((input) => {
            const valuesInUrl = urlParams.getAll(input.name);
            const handle = (input.getAttribute("data-handle") || "").trim();
            const label = (input.getAttribute("data-label") || "").trim();
            const isMatch =
              valuesInUrl.includes(input.value) ||
              (handle && valuesInUrl.includes(handle)) ||
              (label && valuesInUrl.includes(label));
            input.checked = Boolean(isMatch);
          });
      }

      // Runs once for all facet-forms, after the calls made while the page settles
      applyCustomMetafieldFilters() {
        clearTimeout(customFacetState.timer);
        customFacetState.timer = setTimeout(() => this.renderCustomFacets(), 0);
      }

      get customParamNames() {
        const names = new Set();
        document
          .querySelectorAll(
            'form[is="facet-form"] input[data-custom-filter]:not([data-on-sale-filter])',
          )
          .forEach((input) => names.add(input.name));
        document
          .querySelectorAll('form[is="facet-form"] [data-custom-param]')
          .forEach((group) => names.add(group.getAttribute("data-custom-param")));
        return names;
      }

      // Native filters and sorting of the current URL: the part Shopify applies on the server
      customFacetQuery() {
        const params = new URLSearchParams(window.location.search);
        [...this.customParamNames, "filter.on_sale", "page", "section_id", "view"].forEach(
          (name) => params.delete(name),
        );
        params.sort();
        return params.toString();
      }

      // Selected custom values, read from the URL: a selected value may have no checkbox yet
      getCustomSelections() {
        const params = new URLSearchParams(window.location.search);
        const selections = new Map();
        this.customParamNames.forEach((name) => {
          const tokens = new Set();
          params.getAll(name).forEach((value) => {
            if (!value.trim()) return;
            tokens.add(value.toLowerCase().trim());
            tokens.add(customFacetHandle(value));
          });
          if (tokens.size) selections.set(name, tokens);
        });
        return { selections, onSale: params.has("filter.on_sale") };
      }

      updateCustomFilterURL(input) {
        const url = new URL(window.location.href);
        const handle = input.getAttribute("data-handle") || customFacetHandle(input.value);
        const kept = url.searchParams
          .getAll(input.name)
          .filter(
            (value) =>
              value.toLowerCase().trim() !== input.value.toLowerCase().trim() &&
              customFacetHandle(value) !== handle,
          );

        url.searchParams.delete(input.name);
        kept.forEach((value) => url.searchParams.append(input.name, value));
        if (input.checked) url.searchParams.append(input.name, input.value);
        url.searchParams.delete("page");
        url.searchParams.delete("section_id");
        history.replaceState({}, "", url.toString());
      }

      loadCustomFacetData(query) {
        if (!customFacetState.data.has(query)) {
          const request = this.fetchCustomFacetData(query).catch((error) => {
            customFacetState.data.delete(query);
            throw error;
          });
          customFacetState.data.set(query, request);
        }
        return customFacetState.data.get(query);
      }

      async fetchCustomFacetData(query) {
        const base = `${window.location.pathname}?${query ? `${query}&` : ""}view=filter-data`;
        const first = await (await fetch(`${base}&page=1`)).json();
        const rest = await Promise.all(
          Array.from({ length: first.pages - 1 }, (_, index) =>
            fetch(`${base}&page=${index + 2}`).then((response) => response.json()),
          ),
        );

        return [first, ...rest]
          .flatMap((data) => data.products)
          .map((product) => {
            const values = {};
            Object.entries(product.m).forEach(([key, value]) => {
              values[`filter.p.m.${key}`] = String(value)
                .split(",")
                .map((label) => label.trim())
                .filter(Boolean)
                .map((label) => ({
                  label,
                  key: label.toLowerCase(),
                  handle: customFacetHandle(label),
                }));
            });
            return { id: String(product.id), sale: product.sale, values };
          });
      }

      // Cards of one collection page (with the style block the card snippet puts before each), as HTML keyed by product id
      loadCustomFacetPage(query, page) {
        const key = `${query}|${page}`;
        if (!customFacetState.pages.has(key)) {
          const url = `${window.location.pathname}?${query ? `${query}&` : ""}page=${page}&section_id=${theme.utils.sectionId(this)}`;
          const request = fetch(url)
            .then((response) => response.text())
            .then((responseText) => {
              const parsedHTML = new DOMParser().parseFromString(responseText, "text/html");
              return new Map(
                Array.from(
                  parsedHTML.querySelectorAll("#ProductGridContainer .product-card[data-product-id]"),
                  (card) => {
                    const style = card.previousElementSibling?.tagName === "STYLE" ? card.previousElementSibling.outerHTML : "";
                    return [card.getAttribute("data-product-id"), style + card.outerHTML];
                  },
                ),
              );
            })
            .catch((error) => {
              customFacetState.pages.delete(key);
              throw error;
            });
          customFacetState.pages.set(key, request);
        }
        return customFacetState.pages.get(key);
      }

      matchesCustomFacets(product, selections, onSale, exceptParam = null) {
        if (onSale && !product.sale) return false;
        for (const [param, tokens] of selections) {
          if (param === exceptParam) continue;
          const values = product.values[param];
          if (!values || !values.some((value) => tokens.has(value.key) || tokens.has(value.handle))) {
            return false;
          }
        }
        return true;
      }

      async renderCustomFacets() {
        const container = document.getElementById("ProductGridContainer");
        const groups = document.querySelectorAll('form[is="facet-form"] [data-custom-param]');
        if (!container || !groups.length) return;

        const run = ++customFacetState.run;
        const query = this.customFacetQuery();
        const { selections, onSale } = this.getCustomSelections();
        const isActive = selections.size > 0 || onSale;
        if (isActive) container.classList.add("custom-facet-loading");

        try {
          const products = await this.loadCustomFacetData(query);
          if (run !== customFacetState.run) return;

          this.renderCustomFacetOptions(products, selections, onSale);
          if (isActive) {
            await this.renderCustomFacetResults(products, selections, onSale, query, run);
          } else if (customFacetState.gridFiltered) {
            // Last custom filter removed: bring back the page Shopify renders
            customFacetState.gridFiltered = false;
            const url = new URL(window.location.href);
            url.searchParams.set("section_id", theme.utils.sectionId(this));
            this.renderSection(url.toString());
          }
        } catch (error) {
          console.error(error);
        } finally {
          if (run === customFacetState.run) {
            container.classList.remove("custom-facet-loading");
            this.renderCustomActiveFacets();
          }
        }
      }

      renderCustomFacetOptions(products, selections, onSale) {
        const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: "base" });
        // Numeric-first compare: labels starting with a number (e.g. "1.28m", "1.5x", "f/1.8", "24-70mm")
        // sort by their real numeric value; everything else keeps natural text order, after the numbers.
        const facetNum = (s) => {
          const m = /^\s*(?:f\/?\s*)?(\d+(?:\.\d+)?)/i.exec(s);
          return m ? parseFloat(m[1]) : null;
        };
        const facetCompare = (a, b) => {
          const x = facetNum(a);
          const y = facetNum(b);
          if (x !== null && y !== null && x !== y) return x - y;
          if ((x === null) !== (y === null)) return x === null ? 1 : -1;
          return collator.compare(a, b);
        };

        const focusedId = document.activeElement?.id;
        const optionsByParam = new Map();

        document.querySelectorAll('form[is="facet-form"] [data-custom-param]').forEach((group) => {
          const param = group.getAttribute("data-custom-param");
          const list = group.querySelector("ul.list");
          if (!list) return;

          if (!optionsByParam.has(param)) {
            // One option per handle, so values typed differently ("2-3" / "2/3", "F1.8" / "f1.8")
            // merge, as selecting either already matches both. Counted over the products that match
            // every other selected filter, labelled with the most used spelling.
            const options = new Map();
            products.forEach((product) => {
              const values = product.values[param];
              if (!values) return;
              const matches = this.matchesCustomFacets(product, selections, onSale, param);
              const counted = new Set();
              values.forEach((value) => {
                const id = value.handle || value.key;
                if (!options.has(id)) options.set(id, { ...value, count: 0, spellings: new Map() });
                const option = options.get(id);
                option.spellings.set(value.label, (option.spellings.get(value.label) || 0) + 1);
                if (matches && !counted.has(id)) {
                  option.count++;
                  counted.add(id);
                }
              });
            });
            options.forEach((option) => {
              option.label = [...option.spellings].sort((a, b) => b[1] - a[1])[0][0];
              option.key = option.label.toLowerCase();
            });
            optionsByParam.set(
              param,
              [...options.values()].sort((a, b) => facetCompare(a.label, b.label)),
            );
          }

          const options = optionsByParam.get(param);
          const tokens = selections.get(param) || new Set();
          const index = group.getAttribute("data-index");
          group.hidden = options.length === 0;
          list.innerHTML = options
            .map((option, i) => {
              const id = customFacetEscape(`${index}-${i + 1}`);
              const checked = tokens.has(option.key) || tokens.has(option.handle);
              const disabled = option.count === 0 && !checked;
              return `<li class="flex items-center gap-3">
                <input name="${customFacetEscape(param)}" value="${customFacetEscape(option.label)}" data-label="${customFacetEscape(option.label)}" data-handle="${customFacetEscape(option.handle)}" data-custom-filter="true" class="checkbox" id="${id}" type="checkbox"${checked ? " checked" : ""}${disabled ? " disabled" : ""} />
                <label for="${id}" class="flex items-baseline gap-1 cursor-pointer${disabled ? " opacity-50" : ""}">${customFacetEscape(option.label)}<span class="text-sm text-opacity">(${option.count})</span></label>
              </li>`;
            })
            .join("");

          const search = group.querySelector(".facet-options-search input");
          if (search?.value) search.dispatchEvent(new Event("input"));
        });

        if (focusedId) document.getElementById(focusedId)?.focus({ preventScroll: true });
      }

      async renderCustomFacetResults(products, selections, onSale, query, run) {
        const container = document.getElementById("ProductGridContainer");
        const grid = container.querySelector("motion-list.card-grid");
        if (!grid) return;

        const perPage = parseInt(container.getAttribute("data-products-per-page"), 10) || 15;
        const matched = products.filter((product) =>
          this.matchesCustomFacets(product, selections, onSale),
        );
        const totalPages = Math.max(1, Math.ceil(matched.length / perPage));
        const page = Math.min(
          Math.max(parseInt(new URL(window.location.href).searchParams.get("page"), 10) || 1, 1),
          totalPages,
        );
        const shown = matched.slice((page - 1) * perPage, page * perPage);

        // Each card sits on the collection page that holds its position in the full list
        const positions = new Map(products.map((product, i) => [product.id, i]));
        const cards = await Promise.all(
          shown.map(async (product) => {
            const home = Math.floor(positions.get(product.id) / perPage) + 1;
            for (const candidate of [home, home - 1, home + 1]) {
              if (candidate < 1) continue;
              const card = (await this.loadCustomFacetPage(query, candidate)).get(product.id);
              if (card) return card;
            }
            return "";
          }),
        );
        if (run !== customFacetState.run) return;

        grid.innerHTML = cards.join("");
        customFacetState.gridFiltered = true;

        const emptyState = container.querySelector(".custom-facet-empty-state");
        emptyState?.classList.toggle("hidden", matched.length > 0);
        grid.classList.toggle("hidden", matched.length === 0);

        container.querySelector(".pagination")?.remove();
        if (totalPages > 1) {
          container.insertAdjacentHTML("beforeend", this.customPaginationHTML(page, totalPages));
        }

        const countTemplate = document
          .getElementById("ProductCount")
          ?.getAttribute("data-custom-count");
        const countPill = document.querySelector("#ProductCount .product-count-pill");
        if (countTemplate && countPill) {
          countPill.textContent = countTemplate.replace("[count]", matched.length);
        }
        document.querySelectorAll("results-count").forEach((el) => {
          el.textContent = matched.length;
        });
      }

      // Same markup as snippets/pagination.liquid
      customPaginationHTML(page, totalPages) {
        const url = new URL(window.location.href);
        url.searchParams.delete("section_id");
        const href = (number) => {
          url.searchParams.set("page", number);
          return customFacetEscape(url.pathname + url.search);
        };
        const template = document.getElementById("CustomFacetPaginationIcons");
        const arrowLeft = template?.content.children[0]?.outerHTML || "";
        const arrowRight = template?.content.children[1]?.outerHTML || "";
        const previousText = customFacetEscape(template?.getAttribute("data-previous") || "Previous");
        const nextText = customFacetEscape(template?.getAttribute("data-next") || "Next");

        const numbers = [];
        for (let number = 1; number <= totalPages; number++) {
          if (number === 1 || number === totalPages || Math.abs(number - page) <= 2) {
            numbers.push(number);
          } else if (numbers[numbers.length - 1] !== "…") {
            numbers.push("…");
          }
        }

        const parts = numbers.map((number) => {
          if (number === "…") {
            return '<span class="page flex items-center justify-center rounded-full">…</span>';
          }
          if (number === page) {
            return `<span class="page current flex items-center justify-center rounded-full pointer-events-none" aria-current="page">${number}</span>`;
          }
          return `<a class="page flex items-center justify-center rounded-full" href="${href(number)}" is="magnet-link"><span class="btn-text" data-text>${number}</span></a>`;
        });
        if (page > 1) {
          parts.unshift(
            `<a class="previous flex items-center justify-center gap-2 font-medium" href="${href(page - 1)}" is="magnet-link">${arrowLeft}<span class="btn-text" data-text>${previousText}</span></a>`,
          );
        }
        if (page < totalPages) {
          parts.push(
            `<a class="next flex items-center justify-center gap-2 font-medium" href="${href(page + 1)}" is="magnet-link"><span class="btn-text" data-text>${nextText}</span>${arrowRight}</a>`,
          );
        }

        return `<div class="pagination flex flex-wrap justify-center align-self-center md:gap-2 text-sm md:text-base" data-custom-pagination>${parts.join("")}</div>`;
      }

      setupCustomFilterListeners() {
        if (this._customListenersBound) return;
        this._customListenersBound = true;

        document.addEventListener("click", (e) => {
          const pageLink = e.target.closest("[data-custom-pagination] a[href]");
          if (pageLink) {
            e.preventDefault();
            history.replaceState({}, "", pageLink.href);
            this.applyCustomMetafieldFilters();
            document.querySelector(".collection")?.scrollIntoView({
              behavior: theme.config.motionReduced ? "auto" : "smooth",
            });
            return;
          }

          const clearBtn = e.target.closest(".clear-all-custom-facets-btn");
          if (clearBtn) {
            e.preventDefault();
            this.clearAllFacets();
            return;
          }

          const facetRemove = e.target.closest("facet-remove");
          if (facetRemove) {
            const isClearAll =
              facetRemove.classList.contains("clear-all") ||
              facetRemove.classList.contains("facet-clear-all");

            if (isClearAll) {
              document
                .querySelectorAll("input[data-custom-filter]:checked")
                .forEach((cb) => {
                  cb.checked = false;
                });
              this.dirty = true;
              this.applyCustomMetafieldFilters();
              this.renderCustomActiveFacets();
            }
          }
        });
      }

      renderCustomActiveFacets() {
        const activeFacetsContainer = document.getElementById("FacetFiltersActive");
        if (!activeFacetsContainer) return;

        // Remove any previously rendered custom pills and custom clear-all button
        activeFacetsContainer
          .querySelectorAll(".custom-removable-facet, .custom-facet-clear-all")
          .forEach((el) => el.remove());

        // Gather all checked custom filter inputs across forms
        const allChecked = document.querySelectorAll(
          'form[is="facet-form"] input[data-custom-filter]:checked',
        );

        const seenKeys = new Set();
        const customPills = [];

        allChecked.forEach((cb) => {
          const paramName = cb.name;
          const paramVal = cb.value;
          const dedupeKey = `${paramName}:::${paramVal}`;
          if (seenKeys.has(dedupeKey)) return;
          seenKeys.add(dedupeKey);

          const label = (
            cb.getAttribute("data-label") ||
            cb.value ||
            ""
          ).trim();

          // Create removable facet pill matching native theme markup
          const pill = document.createElement("div");
          pill.className =
            "removable-facet custom-removable-facet flex items-center justify-between gap-3 lg:gap-4 text-xs lg:text-sm leading-none";
          pill.setAttribute("data-param-name", paramName);
          pill.setAttribute("data-param-val", paramVal);

          const titleDiv = document.createElement("div");
          titleDiv.className = "facet-title flex items-center gap-2";
          titleDiv.textContent = label;

          const removeBtn = document.createElement("button");
          removeBtn.type = "button";
          removeBtn.className =
            "block relative custom-facet-remove-btn cursor-pointer bg-transparent border-none p-0 text-inherit";
          removeBtn.setAttribute("aria-label", `Clear ${label}`);
          removeBtn.innerHTML = `
            <span class="btn-text">
              <svg class="icon icon-remove icon-xs" viewBox="0 0 10 10" stroke="currentColor" fill="none" xmlns="http://www.w3.org/2000/svg" role="presentation">
                <path stroke-linecap="round" stroke-linejoin="round" d="m1 9 8-8M1 1l8 8"></path>
              </svg>
            </span>
          `;

          removeBtn.addEventListener("click", (e) => {
            e.preventDefault();
            e.stopPropagation();
            this.removeCustomFilter(paramName, paramVal);
          });

          pill.appendChild(titleDiv);
          pill.appendChild(removeBtn);
          customPills.push(pill);
        });

        // Insert custom pills before the "Clear all" link if present, or append
        const nativeClearAll =
          activeFacetsContainer.querySelector(".facet-clear-all");

        customPills.forEach((pill) => {
          if (nativeClearAll) {
            activeFacetsContainer.insertBefore(pill, nativeClearAll);
          } else {
            activeFacetsContainer.appendChild(pill);
          }
        });

        // Determine total active filter count (native + custom)
        const nativePillsCount = activeFacetsContainer.querySelectorAll(
          ".removable-facet:not(.custom-removable-facet)",
        ).length;
        const totalActiveCount = nativePillsCount + customPills.length;

        // If total active filters > 1 and no native "Clear all" link exists, render one
        if (
          totalActiveCount > 1 &&
          !activeFacetsContainer.querySelector(".facet-clear-all")
        ) {
          const clearAllText =
            activeFacetsContainer.getAttribute("data-clear-all-text") ||
            "Clear all";

          const clearAllBtn = document.createElement("button");
          clearAllBtn.type = "button";
          clearAllBtn.className =
            "facet-clear-all custom-facet-clear-all link text-xs lg:text-sm cursor-pointer bg-transparent border-none p-0 text-inherit";
          clearAllBtn.textContent = clearAllText;

          clearAllBtn.addEventListener("click", (e) => {
            e.preventDefault();
            e.stopPropagation();
            this.clearAllFacets();
          });

          activeFacetsContainer.appendChild(clearAllBtn);
        }
      }

      removeCustomFilter(paramName, paramVal) {
        // 1. Uncheck all matching inputs across all forms
        document
          .querySelectorAll("input[data-custom-filter]")
          .forEach((input) => {
            if (input.name === paramName && input.value === paramVal) {
              input.checked = false;
            }
          });

        // 2. Remove this value from URL parameters
        const url = new URL(window.location.href);
        const currentVals = url.searchParams.getAll(paramName);
        url.searchParams.delete(paramName);
        currentVals.forEach((val) => {
          if (val !== paramVal) {
            url.searchParams.append(paramName, val);
          }
        });
        url.searchParams.delete("page");
        history.replaceState({}, "", url.toString());

        // 3. Re-filter and re-render active pills
        this.applyCustomMetafieldFilters();
        this.renderCustomActiveFacets();
      }

      clearAllFacets() {
        // 1. Uncheck all custom filter inputs across all forms
        document
          .querySelectorAll("input[data-custom-filter]:checked")
          .forEach((cb) => {
            cb.checked = false;
          });

        // 2. Check if any native filters are active
        const nativePill = document.querySelector(
          "#FacetFiltersActive .removable-facet:not(.custom-removable-facet) a[is='facet-remove']",
        );

        if (nativePill) {
          // If native filters are active, fetch clean collection from server
          const cleanUrl = new URL(window.location.href);
          const keysToDelete = [];
          for (const key of cleanUrl.searchParams.keys()) {
            if (key.startsWith("filter.") || key === "page") {
              keysToDelete.push(key);
            }
          }
          keysToDelete.forEach((k) => cleanUrl.searchParams.delete(k));
          cleanUrl.searchParams.set("section_id", theme.utils.sectionId(this));
          this.renderSection(cleanUrl.toString());
        } else {
          // If only custom filters were active, clean the URL and update UI immediately
          const cleanUrl = new URL(window.location.href);
          const keysToDelete = [];
          for (const key of cleanUrl.searchParams.keys()) {
            if (key.startsWith("filter.") || key === "page") {
              keysToDelete.push(key);
            }
          }
          keysToDelete.forEach((k) => cleanUrl.searchParams.delete(k));
          history.replaceState({}, "", cleanUrl.toString());

          this.dirty = true;
          this.applyCustomMetafieldFilters();
          this.renderCustomActiveFacets();
        }
      }

      sortFacetOptionsNaturally() {
        const collator = new Intl.Collator(undefined, {
          numeric: true,
          sensitivity: "base",
        });

        // Numeric-first compare: labels starting with a number (e.g. "1.28m", "1.5x", "f/1.8", "24-70mm")
        // sort by their real numeric value; everything else keeps natural text order, after the numbers.
        const facetNum = (s) => {
          const m = /^\s*(?:f\/?\s*)?(\d+(?:\.\d+)?)/i.exec(s);
          return m ? parseFloat(m[1]) : null;
        };
        const facetCompare = (a, b) => {
          const x = facetNum(a);
          const y = facetNum(b);
          if (x !== null && y !== null && x !== y) return x - y;
          if ((x === null) !== (y === null)) return x === null ? 1 : -1;
          return collator.compare(a, b);
        };

        const optionLists = document.querySelectorAll(
          'form[is="facet-form"] .details__content ul.list, form[is="facet-form"] .details__content ul.swatches',
        );

        optionLists.forEach((ul) => {
          const items = Array.from(ul.querySelectorAll(":scope > li"));
          if (items.length <= 1) return;

          const hasNumericValues = items.some((li) => {
            const text = li.textContent || "";
            return /\d/.test(text);
          });

          if (!hasNumericValues) return;

          items.sort((a, b) => {
            const labelA = (
              a.querySelector("label")?.textContent ||
              a.textContent ||
              ""
            )
              .replace(/\s*\(\d+\)\s*$/, "")
              .trim();

            const labelB = (
              b.querySelector("label")?.textContent ||
              b.textContent ||
              ""
            )
              .replace(/\s*\(\d+\)\s*$/, "")
              .trim();

            return facetCompare(labelA, labelB);
          });

          items.forEach((li) => ul.appendChild(li));
        });
      }
    },
    { extends: "form" },
  );
}

if (!customElements.get("facet-count")) {
  customElements.define(
    "facet-count",
    class FacetCount extends BaseElement {
      connectedCallback() {
        super.connectedCallback();

        const facetUpdateUnsubscriber = theme.pubsub.subscribe(
          theme.pubsub.PUB_SUB_EVENTS.facetUpdate,
          this.onFacetUpdate.bind(this),
        );
        this.registerCleanup(facetUpdateUnsubscriber);
      }

      get itemCount() {
        return parseInt(this.innerText);
      }

      onFacetUpdate(event) {
        const parsedHTML =
          event.parsedHTML ||
          new DOMParser().parseFromString(event.responseText, "text/html");
        const facetCount = parsedHTML.querySelector("facet-count");
        this.innerText = facetCount.innerHTML;
        this.hidden = this.itemCount === 0;
      }
    },
  );
}

if (!customElements.get("results-count")) {
  customElements.define(
    "results-count",
    class ResultsCount extends BaseElement {
      connectedCallback() {
        super.connectedCallback();

        const facetUpdateUnsubscriber = theme.pubsub.subscribe(
          theme.pubsub.PUB_SUB_EVENTS.facetUpdate,
          this.onFacetUpdate.bind(this),
        );
        this.registerCleanup(facetUpdateUnsubscriber);
      }

      onFacetUpdate(event) {
        const parsedHTML =
          event.parsedHTML ||
          new DOMParser().parseFromString(event.responseText, "text/html");
        const resultsCount = parsedHTML.querySelector("results-count");
        this.innerText = resultsCount.innerHTML;
      }
    },
  );
}

if (!customElements.get("facet-remove")) {
  customElements.define(
    "facet-remove",
    class FacetRemove extends MagnetLink {
      connectedCallback() {
        super.connectedCallback();

        this.on(this, "click", this.onClick);
      }

      onClick(event) {
        const form =
          this.closest('form[is="facet-form"]') ||
          document.querySelector('form[is="facet-form"]');

        if (form) {
          event.preventDefault();

          const url = new URL(this.href, window.location.origin);

          const isClearAll =
            this.classList.contains("facet-clear-all") ||
            this.classList.contains("clear-all");

          if (!isClearAll) {
            // Preserve active custom filters in the URL when removing an individual native filter
            document
              .querySelectorAll(
                'form[is="facet-form"] input[data-custom-filter]:checked',
              )
              .forEach((cb) => {
                if (
                  cb.name &&
                  cb.value &&
                  !url.searchParams.getAll(cb.name).includes(cb.value.trim())
                ) {
                  url.searchParams.append(cb.name, cb.value.trim());
                }
              });
          } else {
            // Uncheck custom filters when Clear all is clicked
            document
              .querySelectorAll("input[data-custom-filter]:checked")
              .forEach((cb) => {
                cb.checked = false;
              });
          }

          url.searchParams.set("section_id", theme.utils.sectionId(form));
          form.renderSection(url.toString(), event);
        }
      }
    },
    { extends: "a" },
  );
}

if (!customElements.get("facet-sort")) {
  customElements.define(
    "facet-sort",
    class FacetSort extends BaseElement {
      constructor() {
        super();

        Motion.inView(this, this.init.bind(this), {
          margin: "200px 0px 200px 0px",
        });
      }

      connectedCallback() {
        super.connectedCallback();

        this.on(this, "change", this.onChange);
        this.on(this.button, "click", this.show.bind(this));
        this.on(this.close, "click", this.hide.bind(this));
        this.on(document, "click", this.onWindowClick.bind(this));
      }

      get listbox() {
        return this.querySelector(".sort-listbox");
      }

      get selection() {
        return this.querySelector(".sort-selection");
      }

      get button() {
        return this.querySelector(".sort-by");
      }

      get close() {
        return this.querySelector(".sort-close");
      }

      init() {
        this.initButton();
        this.style.setProperty(
          "--facet-listbox-height",
          `${this.listbox.getBoundingClientRect().height.toFixed(1)}px`,
        );
      }

      initButton() {
        const value = this.selection.innerText;
        const width = theme.getElementWidth(this.selection, value);
        this.style.setProperty("--facet-button-width", `${width}px`);
      }

      onChange(event) {
        const form =
          this.closest('form[is="facet-form"]') ||
          document.querySelector('form[is="facet-form"]');

        if (form) {
          const url = new URL(window.location.href);
          url.searchParams.set("sort_by", event.target.value);
          url.searchParams.set("section_id", theme.utils.sectionId(form));
          url.searchParams.delete("page");
          form.renderSection(url.toString(), event);
        }

        this.selection.innerText = event.target.nextElementSibling.innerText;
        this.initButton();
        this.hide();
      }

      show() {
        this.button.setAttribute("open", "");
      }

      hide(immediate = true) {
        if (this.button.hasAttribute("open")) {
          setTimeout(() => {
            this.button.removeAttribute("open");
          }, 100);

          if (
            theme.config.isTouch ||
            document.body.getAttribute("data-button-hover") === "none"
          )
            return;

          const btnFill = this.button.querySelector("[data-fill]");
          Motion.animate(
            btnFill,
            { y: ["0%", immediate ? "0%" : "-76%"] },
            { duration: 0.6, delay: immediate ? 0 : 0.2 },
          );
        }
      }

      onWindowClick(event) {
        if (!this.contains(event.target)) {
          if (this.button.hasAttribute("open")) {
            this.hide(false);
          }
        }
      }
    },
  );
}

if (!customElements.get("facet-sticky")) {
  customElements.define(
    "facet-sticky",
    class FacetSticky extends BaseElement {
      constructor() {
        super();

        const collectionSection = document.querySelector(".collection-section");
        if (collectionSection) {
          const observer = new IntersectionObserver(
            this.handleIntersection.bind(this),
            {
              rootMargin: `-${screen.availHeight - 200}px 0px ${screen.availHeight}px 0px`,
            },
          );
          observer.observe(collectionSection);
          this.registerCleanup(() => observer.disconnect());
        }
      }

      get button() {
        return this.querySelector(".button");
      }

      handleIntersection(entries) {
        if (entries[0].isIntersecting) {
          Motion.animate(
            this.button,
            {
              opacity: 1,
              visibility: "visible",
              transform: ["translateY(15px)", "translateY(0)"],
            },
            { duration: 1, easing: [0.16, 1, 0.3, 1] },
          );
        } else {
          Motion.animate(
            this.button,
            {
              opacity: 0,
              visibility: "hidden",
              transform: ["translateY(0)", "translateY(15px)"],
            },
            { duration: 1, easing: [0.16, 1, 0.3, 1] },
          );
        }
      }
    },
  );
}

if (!customElements.get("price-range")) {
  customElements.define(
    "price-range",
    class PriceRange extends BaseElement {
      connectedCallback() {
        super.connectedCallback();

        this.rangeMin = this.querySelector('input[type="range"]:first-child');
        this.rangeMax = this.querySelector('input[type="range"]:last-child');
        this.inputMin = this.querySelector('input[name="filter.v.price.gte"]');
        this.inputMax = this.querySelector('input[name="filter.v.price.lte"]');

        this.on(this.inputMin, "focus", this.inputMin.select);
        this.on(this.inputMax, "focus", this.inputMax.select);
        this.on(this.inputMin, "change", this.onInputMinChange.bind(this));
        this.on(this.inputMax, "change", this.onInputMaxChange.bind(this));

        this.on(this.rangeMin, "change", this.onRangeMinChange.bind(this));
        this.on(this.rangeMax, "change", this.onRangeMaxChange.bind(this));
        this.on(this.rangeMin, "input", this.onRangeMinInput.bind(this));
        this.on(this.rangeMax, "input", this.onRangeMaxInput.bind(this));
      }

      onInputMinChange(event) {
        event.preventDefault();
        const target = event.target;
        target.value = Math.max(
          Math.min(
            parseInt(target.value || target.min),
            parseInt(this.inputMax.value || target.max) - 1,
          ),
          target.min,
        );
        this.rangeMin.value = target.value;
        this.rangeMin.parentElement.style.setProperty(
          "--range-min",
          `${(parseInt(this.rangeMin.value) / parseInt(this.rangeMin.max)) * 100}%`,
        );
      }

      onInputMaxChange(event) {
        event.preventDefault();
        const target = event.target;
        target.value = Math.min(
          Math.max(
            parseInt(target.value || target.max),
            parseInt(this.inputMin.value || target.min) + 1,
          ),
          target.max,
        );
        this.rangeMax.value = target.value;
        this.rangeMax.parentElement.style.setProperty(
          "--range-max",
          `${(parseInt(this.rangeMax.value) / parseInt(this.rangeMax.max)) * 100}%`,
        );
      }

      onRangeMinChange(event) {
        event.stopPropagation();
        this.inputMin.value = event.target.value;
        this.inputMin.dispatchEvent(new Event("change", { bubbles: true }));
      }

      onRangeMaxChange(event) {
        event.stopPropagation();
        this.inputMax.value = event.target.value;
        this.inputMax.dispatchEvent(new Event("change", { bubbles: true }));
      }

      onRangeMinInput(event) {
        event.target.value = Math.min(
          parseInt(event.target.value),
          parseInt(this.inputMax.value || event.target.max) - 1,
        );
        event.target.parentElement.style.setProperty(
          "--range-min",
          `${(parseInt(event.target.value) / parseInt(event.target.max)) * 100}%`,
        );
        this.inputMin.value = event.target.value;
      }

      onRangeMaxInput(event) {
        event.target.value = Math.max(
          parseInt(event.target.value),
          parseInt(this.inputMin.value || event.target.min) + 1,
        );
        event.target.parentElement.style.setProperty(
          "--range-max",
          `${(parseInt(event.target.value) / parseInt(event.target.max)) * 100}%`,
        );
        this.inputMax.value = event.target.value;
      }
    },
  );
}

if (!customElements.get("infinite-button")) {
  customElements.define(
    "infinite-button",
    class InfiniteButton extends HoverButton {
      constructor() {
        super();

        this.onClickHandler = this.onClick.bind(this);
      }

      connectedCallback() {
        super.connectedCallback();

        this.on(this, "click", this.onClickHandler);

        if (this.getAttribute("mode") == "infinite") {
          Motion.inView(this, this.onClickHandler, {
            margin: "200px 0px 200px 0px",
          });
        }
      }

      disconnectedCallback() {
        super.disconnectedCallback();

        this.loadMoreAbortController?.abort();
      }

      onClick() {
        if (this.hasAttribute("aria-busy")) return;

        this.loadMoreAbortController?.abort();
        this.loadMoreAbortController = new AbortController();

        this.enableLoading();
        const url = this.buildUrl().toString();

        fetch(url, { signal: this.loadMoreAbortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            const parsedHTML = new DOMParser().parseFromString(
              responseText,
              "text/html",
            );
            this.renderPagination(parsedHTML);
            this.renderProductGridContainer(parsedHTML);
          })
          .catch((error) => {
            if (error.name !== "AbortError") console.error(error);
          });
      }

      renderPagination(parsedHTML) {
        const productGridContainer = document.getElementById(
          "ProductGridContainer",
        );
        if (productGridContainer === null) return;

        const destination = productGridContainer.querySelector(".pagination");
        const source = parsedHTML.querySelector(".pagination");

        if (source) {
          destination.innerHTML = source.innerHTML;
        } else {
          destination.remove();
        }
      }

      renderProductGridContainer(parsedHTML) {
        const productGridContainer = document.getElementById(
          "ProductGridContainer",
        );
        if (productGridContainer === null) return;

        const destination = productGridContainer.querySelector("motion-list");
        const source = parsedHTML.querySelector("motion-list");

        if (source && destination) {
          source.querySelectorAll(".card").forEach((item) => {
            destination.appendChild(item);
          });

          destination.reload();
        }
      }

      buildUrl() {
        const url = new URL(this.getAttribute("action"));
        url.searchParams.set("section_id", theme.utils.sectionId(this));
        return url;
      }

      enableLoading() {
        this.classList.add("pointer-events-none");
        this.setAttribute("aria-busy", "true");
      }
    },
    { extends: "button" },
  );
}

if (!customElements.get("facets-topbar")) {
  customElements.define(
    "facets-topbar",
    class FacetsTopbar extends StickyElement {
      positionStickySidebar() {
        const bounding = this.getBoundingClientRect();
        if (bounding.top === 0) {
          document.body.classList.add("facets-topbar--active");
        } else {
          document.body.classList.remove("facets-topbar--active");
        }
      }
    },
  );
}

if (!customElements.get("model-view")) {
  customElements.define(
    "model-view",
    class ModelView extends MagnetButton {
      constructor() {
        super();

        Motion.inView(this, this.init.bind(this), {
          margin: "200px 0px 200px 0px",
        });

        this._open = this.hasAttribute("open");
        this.onClickHandler = this.onClick.bind(this);
        this.textSpans = this.querySelectorAll(".link-text");
      }

      set open(value) {
        if (value !== this._open) {
          this._open = value;
          value ? this.setAttribute("open", "") : this.removeAttribute("open");
        }
      }

      static get observedAttributes() {
        return ["open"];
      }

      get open() {
        return this._open;
      }

      connectedCallback() {
        super.connectedCallback();

        this.on(this, "click", this.onClickHandler);
      }

      attributeChangedCallback(name, oldValue, newValue) {
        if (name === "open") {
          const isActive = newValue === "";
          this.setActiveModel(isActive);
          this.updateAccessibility(isActive);
        }
      }

      init() {
        if (theme.config.hasLocalStorage) {
          const isActive =
            window.localStorage.getItem(
              `${theme.settings.themeName}:model-view`,
            ) || "false";
          this.open = isActive === "true";
        }
      }

      onClick(event) {
        event.preventDefault();
        this.open = !this.open;
      }

      setActiveModel(isActive) {
        const container = document.getElementById("ProductGridContainer");
        container.classList.toggle("lifestyle", isActive);

        container
          .querySelectorAll("video-media")
          .forEach((videoMedia) => videoMedia.pause());

        if (theme.config.hasLocalStorage) {
          window.localStorage.setItem(
            `${theme.settings.themeName}:model-view`,
            isActive,
          );
        }
      }

      updateAccessibility(isActive) {
        if (this.textSpans.length === 2) {
          const [defaultText, activeText] = this.textSpans;
          this.setAttribute(
            "aria-label",
            isActive
              ? activeText.textContent.trim()
              : defaultText.textContent.trim(),
          );
        }
      }
    },
    { extends: "button" },
  );
}

if (!customElements.get("sub-collections")) {
  customElements.define(
    "sub-collections",
    class SubCollections extends BaseElementMixin(HTMLSelectElement) {
      connectedCallback() {
        super.connectedCallback();

        this.on(this, "change", this.onChange);
      }

      onChange() {
        window.location.href = this.value;
      }
    },
    { extends: "select" },
  );
}

if (!customElements.get("facet-search-input")) {
  customElements.define(
    "facet-search-input",
    class FacetSearchInput extends BaseElementMixin(HTMLInputElement) {
      connectedCallback() {
        super.connectedCallback();

        this.clearBtn = this.closest(".field")?.querySelector(
          ".facet-search-clear",
        );
        this.on(this, "input", this.onInput);
        this.on(this, "keydown", this.onKeyDown);

        if (this.clearBtn) {
          this.on(this.clearBtn, "click", this.onClear.bind(this));
        }

        this.updateClearBtn();

        this.onReload = () => this.filterProducts(this.value);
        document.addEventListener("collection:reloaded", this.onReload);
      }

      disconnectedCallback() {
        super.disconnectedCallback();
        if (this.onReload) {
          document.removeEventListener("collection:reloaded", this.onReload);
        }
      }

      onInput() {
        this.updateClearBtn();
        this.syncOtherInputs();
        this.filterProducts(this.value);
      }

      onKeyDown(event) {
        if (event.key === "Enter") {
          event.preventDefault();
          this.filterProducts(this.value);
        }
      }

      onClear(event) {
        if (event) event.preventDefault();
        this.value = "";
        this.updateClearBtn();
        this.syncOtherInputs();
        this.filterProducts("");
        this.focus();
      }

      updateClearBtn() {
        if (this.clearBtn) {
          if (this.value.trim().length > 0) {
            this.clearBtn.classList.remove("hidden");
          } else {
            this.clearBtn.classList.add("hidden");
          }
        }
      }

      syncOtherInputs() {
        const query = this.value;
        document
          .querySelectorAll('input[is="facet-search-input"]')
          .forEach((input) => {
            if (input !== this) {
              input.value = query;
              input.updateClearBtn?.();
            }
          });
      }

      filterProducts(term) {
        const query = (term || "").trim().toLowerCase();
        const container = document.getElementById("ProductGridContainer");
        if (!container) return;

        const productCards = container.querySelectorAll(".product-card");
        let visibleCount = 0;

        productCards.forEach((card) => {
          if (card.classList.contains("custom-facet-hidden")) {
            card.style.display = "none";
            return;
          }
          const cardText = card.textContent.toLowerCase();
          if (!query || cardText.includes(query)) {
            card.style.display = "";
            visibleCount++;
          } else {
            card.style.display = "none";
          }
        });

        let noMatchMessage = container.querySelector(".facet-search-no-match");
        if (query && visibleCount === 0) {
          if (!noMatchMessage) {
            noMatchMessage = document.createElement("div");
            noMatchMessage.className =
              "facet-search-no-match empty-state grid items-center justify-center text-center gap-4 py-12 w-full col-span-full";
            noMatchMessage.innerHTML = `<p class="heading text-xl">No products found</p><p class="text-opacity text-sm">No results matching "${this.escapeHtml(query)}"</p>`;
            const cardGrid = container.querySelector(".card-grid") || container;
            cardGrid.appendChild(noMatchMessage);
          } else {
            noMatchMessage.innerHTML = `<p class="heading text-xl">No products found</p><p class="text-opacity text-sm">No results matching "${this.escapeHtml(query)}"</p>`;
            noMatchMessage.style.display = "";
          }
        } else if (noMatchMessage) {
          noMatchMessage.style.display = "none";
        }
      }

      escapeHtml(str) {
        return str.replace(
          /[&<>"']/g,
          (m) =>
            ({
              "&": "&amp;",
              "<": "&lt;",
              ">": "&gt;",
              '"': "&quot;",
              "'": "&#39;",
            })[m],
        );
      }
    },
    { extends: "input" },
  );
}
